import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @property {HTMLSelectElement} [filterType] - The product type filter select.
 * @property {HTMLSelectElement} [filterVendor] - The vendor filter select.
 * @property {HTMLSelectElement} [filterTitle] - The product title filter select (cart inline search).
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
    requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

    #controller = new AbortController();

    /**
     * @type {AbortController | null}
     */
    #activeFetch = null;

    #emptyStateLoaded = false;

    /** Current pagination page (1-based). */
    #currentPage = 1;

    /** Last search term — survives section morph. */
    #lastSearchTerm = '';

    /** Last filter values — survive section morph. */
    #lastFilterType = '';
    #lastFilterVendor = '';
    #lastFilterTitle = '';

    /** Cache of fetched title options keyed by vendor name. */
    #titlesCache = new Map();

    /**
     * Product ids keyed by exact title, so the title filter can query by id.
     * Search's `title:` field matches tokens, so "Air Optix Plus Hydraglyde"
     * would also return "Air Optix Plus Hydraglyde Multifocal".
     * @type {Map<string, string[]>}
     */
    #titleProductIds = new Map();

    /**
     * Get the dialog component.
     * @returns {DialogComponent | null} The dialog component.
     */
    get dialog() {
        return this.closest('dialog-component');
    }

    connectedCallback() {
        super.connectedCallback();

        const { dialog } = this;
        const { signal } = this.#controller;

        if (this.refs.searchInput.value.length > 0) {
            this.#showResetButton();
        }

        if (dialog) {
            document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
            dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
            dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

            this.addEventListener('click', this.#handleModalClick, { signal });
        } else {
            // Inline mode (no dialog): auto-load initial results
            this.#emptyStateLoaded = true;
            this.#getSearchResults('');
        }
    }

    /**
     * Handles clicks within the predictive search modal to maintain focus on the input
     * @param {MouseEvent} event - The mouse event
     */
    #handleModalClick = (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        const isInteractiveElement =
            target instanceof HTMLButtonElement ||
            target instanceof HTMLAnchorElement ||
            target instanceof HTMLInputElement ||
            target instanceof HTMLSelectElement ||
            target.closest('button') ||
            target.closest('a') ||
            target.closest('input') ||
            target.closest('select');

        if (!isInteractiveElement && this.refs.searchInput) {
            this.refs.searchInput.focus();
        }
    };

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#controller.abort();
    }

    /**
     * Called after a section morph (e.g. cart update).
     * Re-loads search results so they aren't lost when the section re-renders.
     */
    updatedCallback() {
        super.updatedCallback();
        if (!this.dialog) {
            // Restore the search input value and reset button state
            this.refs.searchInput.value = this.#lastSearchTerm;
            if (this.#lastSearchTerm) {
                this.#showResetButton();
            }
            // Restore filter selections that may have been reset by the morph
            if (this.refs.filterType && this.#lastFilterType) {
                this.refs.filterType.value = this.#lastFilterType;
            }
            if (this.refs.filterVendor && this.#lastFilterVendor) {
                this.refs.filterVendor.value = this.#lastFilterVendor;
            }
            if (this.refs.filterTitle && this.#lastFilterVendor) {
                // Re-populate the title list for the persisted vendor, then restore the selected title.
                this.#populateTitlesForVendor(this.#lastFilterVendor, this.#lastFilterTitle);
            }
            this.#getSearchResults(this.#lastSearchTerm);
        }
    }

    /**
     * Handles the CMD+K key combination.
     * @param {KeyboardEvent} event - The keyboard event.
     */
    #handleKeyboardShortcut = (event) => {
        if (event.metaKey && event.key === 'k') {
            this.dialog?.toggleDialog();
        }
    };

    /**
     * Handles the dialog close event.
     */
    #handleDialogClose = () => {
        this.#resetSearch();
    };

    /**
     * Handles the dialog open event — loads all products initially.
     */
    #handleDialogOpen = () => {
        if (!this.#emptyStateLoaded) {
            this.#emptyStateLoaded = true;
            this.#getSearchResults('');
        }
    };

    get #allResultsItems() {
        const containers = Array.from(
            this.querySelectorAll(
                '.predictive-search-results__wrapper-queries, ' +
                '.predictive-search-results__wrapper-products, ' +
                '.predictive-search-results__list'
            )
        );

        const allItems = containers
            .flatMap((container) => {
                if (container.classList.contains('predictive-search-results__wrapper-products')) {
                    return Array.from(container.querySelectorAll('.predictive-search-results__card'));
                }
                return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
            })
            .filter((item) => item instanceof HTMLElement);

        return /** @type {HTMLElement[]} */ (allItems);
    }

    /**
     * Track whether the last interaction was keyboard-based
     * @type {boolean}
     */
    #isKeyboardNavigation = false;

    get #currentIndex() {
        return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
    }

    set #currentIndex(index) {
        if (!this.#allResultsItems?.length) return;

        let activeItem = null;

        this.#allResultsItems.forEach((item) => {
            item.classList.remove('keyboard-focus');
        });

        for (const [itemIndex, item] of this.#allResultsItems.entries()) {
            if (itemIndex === index) {
                item.setAttribute('aria-selected', 'true');
                if (this.#isKeyboardNavigation) {
                    item.classList.add('keyboard-focus');
                }
                activeItem = item;
            } else {
                item.removeAttribute('aria-selected');
            }
        }

        activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
        this.refs.searchInput.focus();
    }

    get #currentItem() {
        return this.#allResultsItems?.[this.#currentIndex];
    }

    /**
     * Navigate through the predictive search results using arrow keys or close them with the Escape key.
     * @param {KeyboardEvent} event - The keyboard event.
     */
    onSearchKeyDown = (event) => {
        if (event.key === 'Escape') {
            this.#resetSearch();
            return;
        }

        if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            return;
        }

        const currentIndex = this.#currentIndex;
        const totalItems = this.#allResultsItems.length;

        switch (event.key) {
            case 'ArrowDown':
                this.#isKeyboardNavigation = true;
                event.preventDefault();
                this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
                break;

            case 'Tab':
                if (event.shiftKey) {
                    this.#isKeyboardNavigation = true;
                    event.preventDefault();
                    this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
                } else {
                    this.#isKeyboardNavigation = true;
                    event.preventDefault();
                    this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
                }
                break;

            case 'ArrowUp':
                this.#isKeyboardNavigation = true;
                event.preventDefault();
                this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
                break;

            case 'Enter': {
                const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
                if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
                    event.preventDefault();
                    window.location.href = singleResultContainer.dataset.singleResultUrl;
                    return;
                }

                if (this.#currentIndex >= 0) {
                    event.preventDefault();
                    const suggestionBtn = this.#currentItem?.querySelector('[data-suggestion-query]');
                    if (suggestionBtn) {
                        suggestionBtn.click();
                    } else {
                        this.#currentItem?.querySelector('a')?.click();
                    }
                } else {
                    const searchUrl = new URL(Theme.routes.search_url, location.origin);
                    searchUrl.searchParams.set('q', this.refs.searchInput.value);
                    window.location.href = searchUrl.toString();
                }
                break;
            }
        }
    };

    /**
     * Handles clicking a search suggestion pill — searches within the modal.
     * @param {Event} event - The click event.
     */
    onSuggestionClick(event) {
        event.preventDefault();
        const target = /** @type {HTMLElement} */ (event.target);
        const button = target.closest('[data-suggestion-query]');
        if (!button) return;
        const query = button.dataset.suggestionQuery;
        if (!query) return;

        this.refs.searchInput.value = query;
        this.#showResetButton();
        this.#currentIndex = -1;
        this.#getSearchResults(query);
    }

    /**
     * Handles filter dropdown changes — re-runs the current search with updated filters.
     */
    onFilterChange = () => {
        const searchTerm = this.refs.searchInput.value.trim();
        this.#currentIndex = -1;
        this.#currentPage = 1;
        this.#getSearchResults(searchTerm);
    };

    /**
     * Cart-page handler invoked when the vendor select changes. Clears the dependent
     * title dropdown, repopulates it with the titles of the selected vendor, and re-runs
     * the search.
     */
    onCartVendorChange = () => {
        const vendor = this.refs.filterVendor?.value || '';
        if (this.refs.filterTitle) {
            // Reset selection before fetching so the search query no longer filters by stale title.
            this.refs.filterTitle.value = '';
            this.#populateTitlesForVendor(vendor, '');
        }
        this.onFilterChange();
    };

    /**
     * Reset the title select to its initial "Select a vendor" placeholder state.
     */
    #resetTitleSelect() {
        const select = this.refs.filterTitle;
        if (!select) return;
        select.innerHTML = '<option value="">Select a vendor</option>';
        select.value = '';
        select.disabled = true;
    }

    /**
     * Fetch the product titles for the given vendor and rebuild the title <select>.
     * @param {string} vendor - Vendor name (empty string clears the list).
     * @param {string} [selectedTitle=''] - Title to mark as selected after population.
     */
    async #populateTitlesForVendor(vendor, selectedTitle = '') {
        const select = this.refs.filterTitle;
        if (!select) return;

        if (!vendor) {
            this.#resetTitleSelect();
            return;
        }

        const baseUrl = select.dataset.titlesUrl;
        if (!baseUrl) return;

        // Show a loading state.
        select.innerHTML = '<option value="">Loading…</option>';
        select.disabled = true;

        let titles = this.#titlesCache.get(vendor);
        if (!titles) {
            try {
                const url = new URL(baseUrl, location.origin);
                url.searchParams.set('q', `vendor:"${vendor}"`);
                const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
                if (!response.ok) throw new Error(`Titles fetch failed: ${response.status}`);
                const text = (await response.text()).trim();
                /** @type {Array<{id: number, title: string, handle: string}>} */
                const parsed = JSON.parse(text);
                for (const p of parsed) {
                    if (!p.title || !p.id) continue;
                    const ids = this.#titleProductIds.get(p.title) || [];
                    if (!ids.includes(String(p.id))) ids.push(String(p.id));
                    this.#titleProductIds.set(p.title, ids);
                }
                // Deduplicate titles (multiple variants/products with same title) and sort.
                const seen = new Set();
                titles = parsed
                    .map((p) => p.title)
                    .filter((t) => {
                        if (!t || seen.has(t)) return false;
                        seen.add(t);
                        return true;
                    })
                    .sort((a, b) => a.localeCompare(b));
                this.#titlesCache.set(vendor, titles);
            } catch (error) {
                console.error('[cart-inline-search] Failed to load titles for vendor', vendor, error);
                select.innerHTML = '<option value="">Unable to load titles</option>';
                select.disabled = true;
                return;
            }
        }

        const options = ['<option value="">All titles</option>'];
        for (const title of titles) {
            const escaped = title
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
            options.push(`<option value="${escaped}">${escaped}</option>`);
        }
        select.innerHTML = options.join('');
        select.disabled = titles.length === 0;
        if (selectedTitle) {
            select.value = selectedTitle;
        }
    }

    /**
     * Clears the recently viewed products.
     * @param {Event} event - The event.
     */
    clearRecentlyViewedProducts(event) {
        event.stopPropagation();

        RecentlyViewed.clearProducts();

        const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

        const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

        if (allRecentlyViewedElements.length === 0) {
            return;
        }

        if (recentlyViewedWrapper) {
            recentlyViewedWrapper.classList.add('removing');

            onAnimationEnd(recentlyViewedWrapper, () => {
                recentlyViewedWrapper.remove();
            });
        }
    }

    /**
     * Reset the search state.
     * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
     */
    resetSearch = debounce((keepFocus = true) => {
        if (keepFocus) {
            this.refs.searchInput.focus();
        }
        this.#clearSearchTerm();
    }, 100);

    /**
     * Debounce the search handler to fetch and display search results based on the input value.
     * Reset the current selection index and close results if the search term is empty.
     */
    search = debounce((event) => {
        // If the input is not a text input (like using the Escape key), don't search
        if (!event.inputType) return;

        const searchTerm = this.refs.searchInput.value.trim();
        this.#currentIndex = -1;
        this.#currentPage = 1;

        if (!searchTerm.length) {
            this.#clearSearchTerm();
            return;
        }

        this.#showResetButton();
        this.#getSearchResults(searchTerm);
    }, 200);

    /**
     * Resets scroll positions for search results containers
     */
    #resetScrollPositions() {
        requestAnimationFrame(() => {
            this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
            this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
        });
    }

    /**
     * Check whether any search filters are active.
     * @returns {boolean}
     */
    get #hasActiveFilters() {
        return !!(this.refs.filterType?.value || this.refs.filterVendor?.value || this.refs.filterTitle?.value);
    }

    /**
     * Build the full search query including any active filters.
     * @param {string} searchTerm - The raw search term from the input.
     * @returns {string} The query with filter operators appended.
     */
    #buildFilteredQuery(searchTerm) {
        let query = searchTerm;

        const typeFilter = this.refs.filterType?.value;
        const vendorFilter = this.refs.filterVendor?.value;
        const titleFilter = this.refs.filterTitle?.value;

        // When filters are active we use the regular search endpoint which doesn't
        // do prefix matching on earlier words. Append a wildcard so partial terms
        // like "pro" still match "proclear".
        if (typeFilter || vendorFilter || titleFilter) {
            query = query.replace(/(\S+)/g, '$1*');
        }

        // Title filter is an exact match: query by the product ids that carry
        // this exact title. Placed before the quoted filters so the prefix
        // wildcard (options[prefix]=last) never lands on an id.
        if (titleFilter) {
            const ids = this.#titleProductIds.get(titleFilter);
            if (ids?.length === 1) {
                query += ` id:${ids[0]}`;
            } else if (ids?.length) {
                query += ` (${ids.map((id) => `id:${id}`).join(' OR ')})`;
            } else {
                query += ` title:"${titleFilter}"`;
            }
        }
        if (typeFilter) {
            query += ` product_type:"${typeFilter}"`;
        }
        if (vendorFilter) {
            query += ` vendor:"${vendorFilter}"`;
        }

        return query;
    }

    /**
     * Fetch search results using the section renderer and update the results container.
     * @param {string} searchTerm - The term to search for
     */
    async #getSearchResults(searchTerm) {
        if (!this.dataset.sectionId) return;

        this.#lastSearchTerm = searchTerm;
        this.#lastFilterType = this.refs.filterType?.value || '';
        this.#lastFilterVendor = this.refs.filterVendor?.value || '';
        this.#lastFilterTitle = this.refs.filterTitle?.value || '';

        // Inline mode (no dialog) always uses regular search for full pagination support.
        // Modal uses regular search when filters are active or no search term.
        const isInline = !this.dialog;
        const useRegularSearch = isInline || this.#hasActiveFilters || !searchTerm;
        const url = useRegularSearch
            ? new URL(Theme.routes.search_url, location.origin)
            : new URL(Theme.routes.predictive_search_url, location.origin);

        const query = this.#buildFilteredQuery(searchTerm);
        url.searchParams.set('q', query || '*');

        if (useRegularSearch) {
            url.searchParams.set('type', 'product');
            url.searchParams.set('options[prefix]', 'last');
            if (this.#currentPage > 1) {
                url.searchParams.set('page', String(this.#currentPage));
            }
        } else {
            url.searchParams.set('resources[limit_scope]', 'each');
        }

        const { predictiveSearchResults } = this.refs;

        const abortController = this.#createAbortController();

        sectionRenderer
            .getSectionHTML(this.dataset.sectionId, false, url)
            .then((resultsMarkup) => {
                if (!resultsMarkup) return;

                if (abortController.signal.aborted) return;

                // Results are re-fetched and morphed after every cart update
                // (see updatedCallback). The morph resets each row's variant
                // selects, patient name and quantity to server defaults —
                // clobbering anything the customer picked while the request
                // was in flight, which then adds the WRONG variant to the
                // cart. Snapshot per-product row state and restore it after
                // the morph, like the search term and filters above.
                const rowState = this.#captureRowState();
                morph(predictiveSearchResults, resultsMarkup);
                this.#restoreRowState(rowState);

                this.#resetScrollPositions();
                this.#emitPaginationUpdate();
            })
            .catch((error) => {
                if (abortController.signal.aborted) return;
                throw error;
            });
    }

    /**
     * Capture the user-editable state of every catalog row (variant option
     * selects, prescription property selects, patient name, quantity), keyed
     * by product id.
     * @returns {Map<string, Map<string, string>>}
     */
    #captureRowState() {
        const state = new Map();
        for (const row of this.querySelectorAll('.catalog-row[data-product-id]')) {
            const productId = row.getAttribute('data-product-id');
            if (!productId) continue;

            const fields = new Map();
            for (const field of row.querySelectorAll('select[name], input[name]')) {
                if (!(field instanceof HTMLSelectElement) && !(field instanceof HTMLInputElement)) continue;
                if (field instanceof HTMLInputElement && field.type === 'hidden') continue;
                fields.set(field.name, field.value);
            }
            if (fields.size > 0) state.set(productId, fields);
        }
        return state;
    }

    /**
     * Restore captured row state into rows that still show the same product,
     * then re-dispatch a change event on restored option selects so
     * catalog-row-picker re-derives the hidden variant id from them.
     * @param {Map<string, Map<string, string>>} state
     */
    #restoreRowState(state) {
        if (state.size === 0) return;

        for (const row of this.querySelectorAll('.catalog-row[data-product-id]')) {
            const fields = state.get(row.getAttribute('data-product-id') || '');
            if (!fields) continue;

            /** @type {HTMLSelectElement | null} */
            let changedOptionSelect = null;
            for (const field of row.querySelectorAll('select[name], input[name]')) {
                if (!fields.has(field.name)) continue;
                const saved = fields.get(field.name);
                if (saved === undefined || field.value === saved) continue;

                if (field instanceof HTMLSelectElement) {
                    const exists = Array.from(field.options).some((option) => option.value === saved);
                    if (!exists) continue;
                    field.value = saved;
                    if (field.name.startsWith('options[')) changedOptionSelect = field;
                } else if (field instanceof HTMLInputElement && field.type !== 'hidden') {
                    field.value = saved;
                }
            }

            // The morph also reset the hidden variant id; a change event makes
            // the row's picker re-sync it (and price/stock) from the restored
            // option values.
            changedOptionSelect?.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    /**
     * Reads pagination metadata from the rendered results and dispatches a custom event.
     */
    #emitPaginationUpdate() {
        const el = this.refs.predictiveSearchResults.querySelector('.predictive-search-pagination-data');
        const detail = {
            currentPage: parseInt(el?.dataset.currentPage) || 1,
            totalPages: parseInt(el?.dataset.totalPages) || 1,
            totalItems: parseInt(el?.dataset.totalItems) || 0,
        };
        this.#currentPage = detail.currentPage;
        this.dispatchEvent(new CustomEvent('pagination-update', { detail }));
    }

    /**
     * Navigate to a specific page.
     * @param {number} page - The 1-based page number.
     */
    goToPage(page) {
        this.#currentPage = page;
        const searchTerm = this.refs.searchInput.value.trim();
        this.#getSearchResults(searchTerm);
    }

    /**
     * Fetch the markup for the recently viewed products.
     * @returns {Promise<string | null>} The markup for the recently viewed products.
     */
    async #getRecentlyViewedProductsMarkup() {
        if (!this.dataset.sectionId) return null;

        const viewedProducts = RecentlyViewed.getProducts();
        if (viewedProducts.length === 0) return null;

        const url = new URL(Theme.routes.search_url, location.origin);
        url.searchParams.set('q', viewedProducts.map(/** @param {string} id */(id) => `id:${id}`).join(' OR '));
        url.searchParams.set('resources[type]', 'product');

        return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
    }

    #hideResetButton() {
        const { resetButton } = this.refs;

        resetButton.hidden = true;
    }

    #showResetButton() {
        const { resetButton } = this.refs;

        resetButton.hidden = false;
    }

    #createAbortController() {
        const abortController = new AbortController();
        if (this.#activeFetch) {
            this.#activeFetch.abort();
        }
        this.#activeFetch = abortController;
        return abortController;
    }

    #resetSearch = async () => {
        const { predictiveSearchResults, searchInput } = this.refs;

        this.#currentIndex = -1;
        this.#currentPage = 1;
        searchInput.value = '';
        this.#hideResetButton();
        this.#emptyStateLoaded = false;

        // Reset filters (only on full reset, e.g. closing modal)
        if (this.refs.filterType) this.refs.filterType.value = '';
        if (this.refs.filterVendor) this.refs.filterVendor.value = '';
        if (this.refs.filterTitle) {
            this.#resetTitleSelect();
        }

        if (this.dialog) {
            // Modal mode: clear results
            predictiveSearchResults.innerHTML = '';
        } else {
            // Inline mode: reload unfiltered results
            this.#getSearchResults('');
        }
    };

    /**
     * Clear only the search term but keep filters, then reload products.
     */
    #clearSearchTerm = () => {
        this.#currentIndex = -1;
        this.#currentPage = 1;
        this.refs.searchInput.value = '';
        this.#hideResetButton();

        // Re-fetch with current filters (shows all products if no filters)
        this.#getSearchResults('');
    };
}

if (!customElements.get('predictive-search-component')) {
    customElements.define('predictive-search-component', PredictiveSearchComponent);
}
